import { useMemo, useContext } from 'react';
import { useQuery } from 'react-query';
import createCachedSelector from 're-reselect';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import { mappedArray } from '../../utils/array';
import useCRUD from '../useCRUD';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (data, t) => t,
  (data, t) => {
    // A reason the account was given carries a locale key as its name; one an admin named does not.
    const toOption = ({ id, name, description, countAsNoShow, isGeneric, deletedAt }) => {
      const label = isGeneric ? t(name) : name;

      return {
        value: id,
        // The suffix only ever reaches a historical record: the options below drop deleted reasons
        // before mapping, so nothing offers a retired reason to pick while marking it as retired
        // everywhere it is still read.
        label: deletedAt ? `${label} (${t('deleted')})` : label,
        description,
        countAsNoShow
      };
    };

    const unbookReasons = data || [];

    // Only active reasons can be picked for a new unbooking.
    const unbookReasonsOptions = unbookReasons.filter(({ deletedAt }) => !deletedAt).map(toOption);

    // Label lookup for existing records, deleted reasons included.
    const unbookReasonsMap = mappedArray(unbookReasons, ({ id }) => id, toOption);

    return { unbookReasons, unbookReasonsOptions, unbookReasonsMap };
  }
)({
  keySelector: (data, t, storeKey) => storeKey
});

const useUnbookReasons = () => {
  const { accountId } = useAccount();
  const { t } = useContext(ConfigContext);

  const storeKey = ['unbookReasons', accountId];

  const { isLoading, data } = useQuery(storeKey, () => api.fetchUnbookReasons({ accountId }), {
    enabled: !!accountId,
    select: (unbookReasonsData) => selector(unbookReasonsData, t, storeKey.join('#'))
  });

  const { unbookReasons = [], unbookReasonsOptions = [], unbookReasonsMap = {} } = useMemo(() => {
    const {
      unbookReasons: rawUnbookReasons = [],
      unbookReasonsOptions: rawUnbookReasonsOptions = [],
      unbookReasonsMap: rawUnbookReasonsMap = {}
    } = data || {};
    return {
      unbookReasons: rawUnbookReasons,
      unbookReasonsOptions: rawUnbookReasonsOptions,
      unbookReasonsMap: rawUnbookReasonsMap
    };
  }, [data]);

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: ({ name, description, countAsNoShow }) =>
        api.addUnbookReason({ accountId, name, description, countAsNoShow }),
      editApi: ({ id, description }) => api.updateUnbookReason({ accountId, unbookReasonId: id, description }),
      deleteApi: ({ id }) => api.deleteUnbookReason({ accountId, unbookReasonId: id })
    },
    { refetchOnSuccessDelete: true }
  );

  return {
    isLoading,
    unbookReasons,
    unbookReasonsOptions,
    unbookReasonsMap,
    addUnbookReason: addItem,
    editUnbookReason: editItem,
    deleteUnbookReason: deleteItem
  };
};

export default useUnbookReasons;
